<template>
  <leaflet-map
    ref="fullMap"
    style="height: 80vh;"
    fit-once
    :geolocate="false"
    :show-markers-text="showMarkersText"
    @ready="fetchItemTracking"
    @bounds-updated="setBounds"
    @zoom-updated="setZoom"
    @marker-over="markerOver"
    @marker-out="markerOut"
  >
    <template #controls-topright>
      <div v-if="withTracking && item.id" class="mt-2 leaflet-control-layers leaflet-control-layers-expanded">
        <div>
          <label for="tracking-range" v-translate>Date Range (up to 30 days)</label> <span class="label" v-if="dateRange.dateRangeActive === true">&nbsp;[<a href="#" @click.prevent="resetDateRange" v-translate>reset range</a>] <b-spinner small v-if="trackingWorking"></b-spinner></span>
          <date-range-picker
            id="tracking-range"
            v-model="dateRange.range"
            @update="dateRange.dateRangeActive = true"
            :locale-data="{...dateRange.locale}"
            :opens="dateRange.opens"
            class="mb-2 w-100"
            :ranges="dateRange.ranges"
            :minDate="minDate"
            :maxDate="maxDate"
            @start-selection="setMinDate"
            @finish-selection="setSecondDate"
            @toggle="discernMinDate"
            appendToBody
          >
            <template #input="picker">
              <div style="top: 3px;position:relative">
                <font-awesome-icon :icon="['far', 'calendar-alt']" size="1x" style="vertical-align: middle" />
                <span v-if="!dateRange.dateRangeActive" style="vertical-align: middle">...</span>
                <span v-else style="vertical-align: middle">{{moment(dateRange.range.startDate).format('L')}} - {{moment(dateRange.range.endDate).format('L')}}</span>
              </div>
            </template>
          </date-range-picker>
        </div>
        <b-form-checkbox v-if="showTracking" v-model="showHeathPath" :disabled="trackingWorking">
          <translate>Show heathPath</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showMarkersText" :disabled="trackingWorking">
          <translate>Show Markers' Numbers</translate>
        </b-form-checkbox>
        <b-form-checkbox v-model="showGeofences" :disabled="trackingWorking">
          <translate>Show Geofences</translate>
          <b-spinner small v-if="updating" />
          <strong class="text-danger" v-if="showGeofences && zoom <= 8" :title="$gettext('Zoom-in to Search Again')" v-b-tooltip.hover>(!)</strong>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showList">
          <translate>Show Markers' List</translate>
        </b-form-checkbox>
        <b-form-checkbox v-if="showList" v-model="centerPoints" class="ml-3">
          <translate>Follow Points</translate>
        </b-form-checkbox>
        <b-table id="tracking-table" v-if="showList" :items="tracking" :fields="trackingHistoryListFields" :sort-by.sync="trackingHistoryListSortBy" :sort-desc.sync="trackingHistoryListSortDesc" @row-hovered="rowHovered" @row-unhovered="rowUnhovered" :tbody-tr-class="rowClass" small hover sticky-header class="mt-3">
          <template #cell(data.created_at)="row">
            <span :id="'marker' + row.item.id">
              {{ row.item.data.index }}:
              {{ moment.utc(row.item.data.created_at).tz($tz).format('L LT') }}
            </span>
          </template>
          <template #cell(data.coordinates)="row">
            <b-button size="sm" @click.stop="openMapOn(row.item.data.lat + ',' + row.item.data.lng + '/@' + row.item.data.lat + ',' + row.item.data.lng + ',17z')" class="py-0 px-1" :title="$gettext('Quick view map on new window')">
              <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px; vertical-align: middle;" />
            </b-button>
            <span class="text-nowrap">{{ row.item.data.lat }}, {{ row.item.data.lng }}</span>
            <div v-if="row.item.data.events?.length">
              <ul class="pl-3 mb-0">
                <li v-for="event in row.item.data.events" :key="row.item.data.id + event.type" v-if="event.type">
                  <span v-if="event.type">{{event.type}}</span>
                  <span v-if="event.externalId">
                    <strong>{{event.externalId}}</strong>
                    {{ event.name }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
        </b-table>
      </div>
    </template>
    <template #map-data>
      <l-geo-json
        v-for="(geofence, i) in geofences"
        :key="'geofence' + i"
        :geojson="geofence"
        :options-style="optionsStyle"
        :options="options"
      />
      <l-geo-json
        v-for="(geofence, i) in loadingZones"
        :key="'loadingzone' + i"
        :geojson="geofence"
        :options-style="optionsStyleLoadingZones"
        :options="options"
      />
    </template>
  </leaflet-map>
</template>

<script>
import {
  ItemService,
  GeofenceService
} from '@/services/api'
import LeafletMap from '@/components/Maps/LeafletMap'
import DateRangePicker from 'vue2-daterange-picker'
import {
  LGeoJson
} from 'vue2-leaflet'
import debounce from 'debounce'

export default {
  name: 'ItemsTrackingHistory',
  components: {
    LeafletMap,
    DateRangePicker,
    LGeoJson
  },
  props: {
    item: {
      type: Object,
      default: () => {},
      required: true
    },
    withTracking: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      updating: false,
      tracking: [],
      heathPath: [],
      showTracking: false,
      showHeathPath: false,
      showMarkersText: true,
      trackingWorking: false,
      dateRange: {
        ranges: {
          [this.$gettext('Today')]: [this.moment().startOf('day').toDate(), this.moment().endOf('day').toDate()],
          [this.$gettext('Yesterday')]: [this.moment().subtract(1, 'days').startOf('day').toDate(), this.moment().subtract(1, 'days').endOf('day').toDate()],
          [this.$gettext('Last 7 days')]: [this.moment().subtract(6, 'day').startOf('day').toDate(), this.moment().subtract(0, 'day').endOf('day').toDate()],
          [this.$gettext('Last 30 days')]: [this.moment().subtract(29, 'day').startOf('day').toDate(), this.moment().subtract(0, 'day').endOf('day').toDate()]
        },
        opens: 'left',
        locale: {
          direction: 'ltr',
          format: this.$gettext('mm/dd/yyyy'),
          separator: ' - ',
          applyLabel: this.$gettext('Apply'),
          cancelLabel: this.$gettext('Cancel'),
          weekLabel: 'W',
          customRangeLabel: 'Custom Range',
          daysOfWeek: this.moment.weekdaysMin(),
          monthNames: this.moment.monthsShort(),
          firstDay: this.moment().day(0) === this.moment().local().weekday(0) ? 0 : 1,
          showWeekNumbers: false
        },
        range: {
          startDate: this.moment.utc(this.item.last_position_updated_at).subtract(1, 'day').tz(this.$tz).startOf('day').toDate(),
          endDate: this.moment.utc(this.item.last_position_updated_at).tz(this.$tz).endOf('day').toDate()
        },
        dateRangeActive: true
      },
      minDate: null,
      dateSelectionComplete: false,
      bounds: {},
      zoom: 0,
      showGeofences: false,
      geofences: [],
      optionsStyle: {
        weight: 1,
        color: '#00cccc',
        opacity: 1,
        fillColor: '#00cccc',
        fillOpacity: 0.2
      },
      loadingZones: [],
      optionsStyleLoadingZones: {
        weight: 1,
        color: '#cc00cc',
        opacity: 1,
        fillColor: '#cc00cc',
        fillOpacity: 0.2
      },
      interval: null,
      lastTrackingPoint: {},
      trackingHistoryList: [],
      trackingHistoryListFields: [
        {
          key: 'data.created_at',
          label: this.$gettext('Date'),
          sortable: true
        },
        {
          key: 'data.coordinates',
          label: this.$gettext('Info')
        }
      ],
      trackingHistoryListSortBy: 'data.created_at',
      trackingHistoryListSortDesc: true,
      showList: false,
      centerPoints: true
    }
  },
  computed: {
    maxDate() {
      return this.minDate ? this.moment(this.minDate).add(29, 'days').format('YYYY-MM-DD') : null
    },
    options() {
      return {
        onEachFeature: this.onEachFeatureFunction
      }
    },
    onEachFeatureFunction() {
      return (feature, layer) => {
        let tooltip = ''
        if (feature.properties.externalId) {
          tooltip += `<strong>${feature.properties.externalId}</strong><br>`
        }
        tooltip += feature.properties.name
        if (feature.properties.referenceLocation) {
          tooltip += ` (${feature.properties.referenceLocation})`
        }
        layer.bindTooltip(
          tooltip,
          { permanent: false, sticky: false }
        )
        if (feature.properties.type === 'polygon' && feature.properties.level === 0) {
          const mssg = `<button title="${this.$gettext('Edit this GeoFence')}" type="button" class="btn btn-dark" onclick="window.open('${this.$router.resolve({name: 'GeofencesEdit', params: {'id': feature.properties.id}}).href}')"><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="edit" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" class="svg-inline--fa fa-edit fa-w-18"><path fill="currentColor" d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.3 15.2-40 0-55.2zM384 346.2V448H64V128h229.8c3.2 0 6.2-1.3 8.5-3.5l40-40c7.6-7.6 2.2-20.5-8.5-20.5H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V306.2c0-10.7-12.9-16-20.5-8.5l-40 40c-2.2 2.3-3.5 5.3-3.5 8.5z" class=""></path></svg></button>`

          layer.bindPopup(
            `<div class="text-center">${mssg}</div>`,
            { permanent: false, sticky: false, closeButton: false }
          )
        }
      }
    }
  },
  watch: {
    'dateRange.dateRangeActive': {
      deep: true,
      handler(val) {
        if (val) {
          this.showTracking = true
        } else {
          this.showTracking = false
        }
      }
    },
    'dateRange.range': {
      deep: true,
      handler(val) {
        this.fitted = false
        if (val && this.dateRange.dateRangeActive) {
          this.showTracking = false
          this.$nextTick(() => {
            this.showTracking = true
            this.fetchItemTracking()
          })
        } else {
          this.showTracking = false
        }
      }
    },
    tracking: {
      deep: true,
      handler(val) {
        setTimeout(function () {
          this.$refs.fullMap.addTracking(val)
        }.bind(this, val), 500)
        if (val.length === 0) {
          this.$refs.fullMap.removeHeathPath()
        }
      }
    },
    heathPath(val) {
      if (val) {
        this.$refs.fullMap.addHeathPath(this.heathPath)
      } else {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    showTracking(val) {
      clearInterval(this.interval)
      if (val && this.dateRange.dateRangeActive) {
        this.fetchItemTracking()
      } else {
        this.tracking = []
        this.showHeathPath = false
      }
      this.interval = setInterval(() => {
        this.fetchItemTracking()
      }, 60000)
    },
    showHeathPath(val) {
      if (val) {
        this.heathPath = this.tracking.map(x => {
          return [x.lat, x.lng, x.z]
        })
      } else {
        this.heathPath = []
      }
    },
    showGeofences(val) {
      if (val) {
        this.fetchGeofences()
      } else {
        this.geofences = []
      }
    },
    zoom(val) {
      if (this.showGeofences) {
        debounce(this.fetchGeofences(), 500)
      }
    },
    bounds(val) {
      if (this.showGeofences) {
        debounce(this.fetchGeofences(), 500)
      }
    }
  },
  mounted () {
    this.showTracking = true
    this.interval = setInterval(() => {
      this.fetchItemTracking()
    }, 60000)
  },
  beforeDestroy () {
    clearInterval(this.interval)
  },
  methods: {
    rowHovered(item, index, event) {
      this.$refs.fullMap.map.eachLayer((layer) => {
        if (layer.options.pane === 'tooltipPane') layer.removeFrom(this.$refs.fullMap.map)
      })
      item.icon.size = [20, 20]
      item.icon.anchor = [10, 10]
      this.$refs.fullMap.$refs['marker' + item.id][0].$refs.theMarker.mapObject.openTooltip()
      if (this.centerPoints) {
        this.$refs.fullMap.center = [item.lat, item.lng]
      }
      return item
    },
    rowUnhovered(item, index, event) {
      item.icon.size = [10, 10]
      item.icon.anchor = [5, 5]
      this.$nextTick(() => {
        this.$refs.fullMap.$refs['marker' + item.id][0].$refs.theMarker.mapObject.closeTooltip()
      })
      return item
    },
    markerOver(item, event) {
      this.$refs.fullMap.map.eachLayer((layer) => {
        if (layer.options.pane === 'tooltipPane') layer.removeFrom(this.$refs.fullMap.map)
      })
      this.tracking = this.tracking.map(x => {
        x.hover = false
        return x
      })
      let index = this.tracking.findIndex(x => x.id === item.id)
      if (index !== -1) {
        this.$set(this.tracking[index], 'hover', true)
        this.$set(this.tracking[index].icon, 'size', [20, 20])
        this.$set(this.tracking[index].icon, 'anchor', [10, 10])

        const table = document.getElementById('tracking-table')
        if (table) {
          table.parentElement.scrollTo({
            top: document.getElementById('marker' + item.id).parentElement.offsetTop,
            left: 0,
            behavior: 'smooth'
          })
        }
      }
    },
    markerOut(item, event) {
      let index = this.tracking.findIndex(x => x.id === item.id)
      if (index !== -1) {
        this.$set(this.tracking[index], 'hover', false)
        this.$set(this.tracking[index].icon, 'size', [10, 10])
        this.$set(this.tracking[index].icon, 'anchor', [5, 5])
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.hover === true) return 'hover'
    },
    setMinDate(e) {
      this.minDate = this.moment(e).format('YYYY-MM-DD')
    },
    setSecondDate(e) {
      this.dateSelectionComplete = true
    },
    discernMinDate(show, e) {
      if (show === false && (!this.dateSelectionComplete || (this.dateSelectionComplete && !this.dateRangeActive))) {
        this.minDate = null
      }
    },
    resetDateRange() {
      this.dateRange.range = {
        startDate: this.moment().startOf('day').toDate(),
        endDate: this.moment().endOf('day').toDate()
      }
      this.dateRange.dateRangeActive = false
      this.minDate = null
      this.dateSelectionComplete = false
    },
    addItemToMap() {
      let item = this.item
      item.item_tracking = this.lastTrackingPoint || this.item.item_tracking
      let infoWindowHtml = ''
      infoWindowHtml += item.reference_code
      let itemColor
      if (this.ecosystemModules.items.icon.colorByMinAge) {
        const daysIdle = this.elapsedTimeRaw(item.last_position_updated_at) / 86400000
        itemColor = this.itemColorByAge(daysIdle, 'items')
      }

      let direction = 0
      if (this.ecosystemModules.items.directionableBy) {
        direction = this.getItemDirection(this.ecosystemModules.items.directionableBy.split('.').reduce((o, i) => o[i], item))
      }

      let dynamic = null
      try {
        if (this.ecosystemModules.items.dynamicableBy) {
          dynamic = this.getItemDynamic(this.ecosystemModules.items.dynamicableBy.split('.').reduce((o, i) => o[i], item))
        }
      } catch (e) {
        console.log(e)
      }

      let load = null
      try {
        if (this.ecosystemModules.items.loadableBy) {
          load = this.ecosystemModules.items.loadableBy.split('.').reduce((o, i) => o[i], item)
        }
      } catch (e) {
        console.assert(e)
      }

      let marker = {
        id: 100000,
        cardType: 'item',
        lat: item[this.ecosystemModules.items.geolocalizableBy].lat || item.lat,
        lng: item[this.ecosystemModules.items.geolocalizableBy].lng || item.lng,
        icon: {
          type: this.ecosystemModules.items.icon.type,
          size: this.ecosystemModules.items.icon.size,
          anchor: this.ecosystemModules.items.icon.anchor,
          popupAnchor: this.ecosystemModules.items.icon.popupAnchor,
          color: item.active ? (itemColor || '#000') : '#333',
          text: this.ecosystemModules.items.icon.text || item.reference_code,
          direction,
          dynamic,
          load
        },
        x: 30,
        y: 30,
        optimized: false,
        infoText: {
          id: item.id,
          type: 'item',
          html: infoWindowHtml,
          color: item.active ? itemColor : '#333',
          data: item,
          isShared: item.isShared || false
        },
        popup: true
      }

      this.$refs.fullMap.addMarkers([marker])
      if (this.fitted === false) {
        this.$refs.fullMap.fitted = false
        this.fitted = true
      }
    },
    setBounds($event) {
      this.bounds = $event
    },
    setZoom($event) {
      this.zoom = $event
    },
    fetchItemTracking() {
      this.trackingWorking = true
      const params = {
        page: 0,
        limit: 1,
        date_from: this.moment(this.dateRange.range.startDate).utc().format('YYYY-MM-DD HH:mm:ss'),
        date_to: this.moment(this.dateRange.range.endDate).utc().format('YYYY-MM-DD HH:mm:ss'),
        filter_groups: [
          {
            filters: [
              ['item_id', 'eq', this.item.id]
            ]
          }
        ],
        includes: ['events_history.owner']
      }

      ItemService.getTrackingHistory({params})
        .then(response => {
          let markers = []

          let uniqueTrackingPointsByGeo = response.body.data.filter((thing, index, self) =>
            index === self.findIndex((t) => {
              return thing.lat === t.lat && thing.lng === t.lng
            })
          ).map(x => {
            if (x.events_history?.length) {
              x.events_history = x.events_history.sort((a, b) => b.event_name === 'entry' ? -1 : 1)
            }

            return x
          })

          let lastEvents = []
          const leaving = this.$gettext('LEAVING')
          const entering = this.$gettext('ARRIVING')
          // const outside = this.$gettext('OUTSIDE')
          const inside = this.$gettext('INSIDE')
          uniqueTrackingPointsByGeo = uniqueTrackingPointsByGeo.map(x => {
            let currentEvents = []
            if (x.events_history?.length) {
              for (let i = 0; i < x.events_history.length; i++) {
                const element = x.events_history[i]
                if (element.event_name === 'exit') {
                  currentEvents[i] = {
                    type: leaving,
                    externalId: element.owner.external_id,
                    name: element.owner.name
                  }
                } else if (element.event_name === 'entry') {
                  currentEvents[i] = {
                    type: entering,
                    externalId: element.owner.external_id,
                    name: element.owner.name
                  }
                }
              }
            } else if (!lastEvents[1] && (lastEvents[0]?.type === leaving || !lastEvents[0]?.type)) {
              currentEvents[0] = null
              // {
              //   // type: outside,
              //   // externalId: lastEvents[0].externalId,
              //   // name: lastEvents[0].name
              // }
            } else if (lastEvents[0]?.type === entering || lastEvents[1]?.type === entering || lastEvents[0]?.type === inside) {
              currentEvents[0] = {
                type: inside,
                externalId: lastEvents[1]?.type === entering ? lastEvents[1].externalId : lastEvents[0].externalId,
                name: lastEvents[1]?.type === entering ? lastEvents[1].name : lastEvents[0].name
              }
            }
            x.events = currentEvents.filter(Boolean)
            lastEvents = currentEvents.filter(Boolean)
            return x
          })

          // Fill first trackings if we can assume the item was inside something
          const firstEvent = uniqueTrackingPointsByGeo.find(x => x.events.length)
          const firstEventIndex = uniqueTrackingPointsByGeo.findIndex(x => x.events.length)

          if (firstEvent?.events[0].type === leaving) {
            uniqueTrackingPointsByGeo = uniqueTrackingPointsByGeo.map((x, i) => {
              if (i < firstEventIndex && !x.events.length) {
                x.events[0] = {
                  type: inside,
                  externalId: firstEvent.events[0].externalId,
                  name: firstEvent.events[0].name
                }
              }
              return x
            })
          }

          this.trackingHistoryList = uniqueTrackingPointsByGeo
          this.trackingWorking = false

          let timeThreshold = null
          uniqueTrackingPointsByGeo.forEach((item, i) => {
            if (timeThreshold === null || parseInt(item.created_at.replace(/\D/g, '')) > timeThreshold) {
              markers.push({
                id: 1000 + i,
                lat: item.lat,
                lng: item.lng,
                icon: {
                  type: 'marker-circle',
                  size: [10, 10],
                  anchor: [5, 5],
                  color: '#f0f',
                  text: (i + 1)
                },
                x: 11,
                y: 34,
                data: {...item, index: (i + 1)},
                infoText: this.setInfoText(item, i),
                tooltip: true,
                z: uniqueTrackingPointsByGeo[(i + 1)] ? this.getSpeed(item, uniqueTrackingPointsByGeo[(i + 1)]) : 0
              })

              if (uniqueTrackingPointsByGeo.length === i + 1) {
                this.lastTrackingPoint = item.item_tracking
              }

              timeThreshold = parseInt(item.created_at.replace(/\D/g, '')) + 1
            }
          })
          this.tracking = markers
          this.addItemToMap()
        })
    },
    setInfoText(item, i) {
      let html = `${(i + 1)}: ${this.moment.utc(item.created_at).tz(this.$tz).format('L LT')}`
      if (item.events?.length) {
        html += '<ul class="pl-3 mb-0">'
        for (const event of item.events) {
          html += `<li>${event.type} ${event.externalId ? '<strong>' + event.externalId + '</strong> ' + event.name : '<strong>' + event.externalId || '' + '</strong> ' + event.name || ''}<br>(${item.lat},${item.lng})</li>`
        }
        html += '</ul>'
      }
      return html
    },
    fetchGeofences() {
      if (this.zoom > 8 && !this.updating) {
        this.updating = true
        let params = {
          fields: 'id,external_id,name,reference_location,fence,geo_type.name,geo_type.type',
          filter_groups: [
            {
              filters: [
                ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng],
                ['geo_type.level', 'eq', 1]
              ]
            }
          ]
        }
        GeofenceService.getAll({params})
          .then(async response => {
            this.loadingZones = response.body.data.map(x => {
              return {
                type: 'Feature',
                geometry: x.fence,
                properties: {
                  id: x.id,
                  externalId: x.external_id,
                  name: x.name,
                  type: x.geo_type.type,
                  typeName: x.geo_type.name,
                  referenceLocation: x.reference_location,
                  level: 1
                }
              }
            })
            let params = {
              fields: 'id,external_id,name,reference_location,fence,geo_type.name,geo_type.type',
              filter_groups: [
                {
                  filters: [
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng],
                    ['geo_type.level', 'eq', false]
                  ]
                }
              ]
            }
            await GeofenceService.getAll({params})
              .then(response => {
                this.geofences = response.body.data.map(x => {
                  return {
                    type: 'Feature',
                    geometry: x.fence,
                    properties: {
                      id: x.id,
                      externalId: x.external_id,
                      name: x.name,
                      type: x.geo_type.type,
                      typeName: x.geo_type.name,
                      referenceLocation: x.reference_location,
                      level: 0
                    }
                  }
                })
              })
          })
          .finally(() => {
            this.updating = false
          })
      }
    }
  }
}
</script>
